"use client";

import { useState, type FormEvent } from "react";
import type { RegisterPayload, Gender } from "@/types";

interface RegisterStepProps {
  onSubmit: (payload: RegisterPayload) => void;
  isPending: boolean;
  error: Error | null;
}

export function RegisterStep({ onSubmit, isPending, error }: RegisterStepProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [gender, setGender] = useState<Gender | "">("");
  const [birthdate, setBirthdate] = useState("");

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const payload: RegisterPayload = { name };
    if (email) payload.email = email;
    if (gender) payload.gender = gender;
    if (birthdate) payload.birthdate = birthdate;
    onSubmit(payload);
  };

  const isValid = name.trim().length >= 2;

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="text-center">
        <h2 className="text-xl font-bold text-headline">Créer votre compte</h2>
        <p className="mt-1 text-sm text-body">
          Quelques informations pour commencer.
        </p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-headline mb-1.5">
          Nom complet <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nom et prénom"
          className="w-full rounded-lg border border-gray-200 dark:border-[#313131] dark:bg-[#313131] dark:text-gray-400 px-3 py-2.5 text-sm focus:outline-none hover:border-black focus:border-black dark:focus:border-white dark:hover:border-gray-400 dark:hover:border-gray-500 dark:focus:border-gray-400"
          autoFocus
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-headline mb-1.5">
          Email
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="john@example.com"
          className="w-full rounded-lg border border-gray-200 dark:border-[#313131] dark:bg-[#313131] dark:text-gray-400 px-3 py-2.5 text-sm focus:outline-none hover:border-black focus:border-black dark:focus:border-white dark:hover:border-gray-400 dark:hover:border-gray-500 dark:focus:border-gray-400"
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-headline mb-1.5">
          Genre
        </label>
        <div className="flex gap-3">
          {([["male", "Homme"], ["female", "Femme"], ["other", "Autre"]] as const).map(([g, label]) => (
            <button
              key={g}
              type="button"
              onClick={() => setGender(g)}
              className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                gender === g
                  ? "border-primary bg-primary/10 text-secondary "
                  : "border-gray-300 dark:border-[#313131] text-gray-600 dark:text-gray-400 dark:text-gray-500 hover:border-gray-400 dark:hover:border-gray-500"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-headline mb-1.5">
          Date de naissance
        </label>
        <input
          type="date"
          value={birthdate}
          onChange={(e) => setBirthdate(e.target.value)}
          className="w-full rounded-lg border border-gray-200 dark:border-[#313131] dark:bg-[#313131] dark:text-gray-400 px-3 py-2.5 text-sm focus:outline-none hover:border-black focus:border-black dark:focus:border-white dark:hover:border-gray-400 dark:hover:border-gray-500 dark:focus:border-gray-400"
        />
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
          {error.message}
        </div>
      )}

      <button
        type="submit"
        disabled={!isValid || isPending}
        className="w-full rounded-full bg-secondary py-3 text-sm font-bold text-white transition hover:bg-secondary/90 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isPending ? "Création du compte..." : "S'inscrire"}
      </button>
    </form>
  );
}
